import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import SlidewordsHUD from "./SlidewordsHUD";
import BaseSlidewords from "./BaseSlidewords";
import {
  getPonderStreakFromStorage,
  updatePonderStreak,
  getIsPonderStreakActive,
} from "../components/PonderStreakUtil";
import { getCachedWordsForDate } from "./slidewordsHelper";
import CalendarArchive from "../components/CalendarArchive";
import { formatDateForStorage } from "../../../components/GameStateUtil";
import CircularProgress from "@mui/material/CircularProgress";

const GAME_ID = "slidewords";
const BEST_SCORE_KEY = "slidewords-bestScore";

// Calculate goal based on total possible words
const calculateGoal = (totalPossible) => {
  if (totalPossible <= 3) return 2;
  if (totalPossible <= 5) return Math.floor(totalPossible * 0.4); // 2 for 5 words
  if (totalPossible <= 10) return Math.floor(totalPossible * 0.4);
  if (totalPossible <= 20) return Math.floor(totalPossible * 0.35);
  return Math.floor(totalPossible * 0.3);
};

const Slidewords = () => {
  const [wordsFound, setWordsFound] = useState(0);
  const [bestWords, setBestWords] = useState(() => {
    const saved = localStorage.getItem(BEST_SCORE_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [attempts, setAttempts] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [gameHistory, setGameHistory] = useState({});
  const [loadingWords, setLoadingWords] = useState(true);
  const [sourceWords, setSourceWords] = useState([]);
  const [possibleWords, setPossibleWords] = useState([]);
  const [totalPossible, setTotalPossible] = useState(0);

  // Load words for current date (async)
  useEffect(() => {
    let isMounted = true;
    setLoadingWords(true);
    getCachedWordsForDate(currentDate).then((result) => {
      if (isMounted) {
        setSourceWords(result.sourceWords);
        setPossibleWords(result.possibleWords);
        setTotalPossible(result.totalPossible);
        setLoadingWords(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [currentDate]);

  // Update streak on mount and date change
  useEffect(() => {
    const { count } = getPonderStreakFromStorage(GAME_ID);
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive(GAME_ID));
  }, [currentDate]);

  // Update best words if wordsFound increases
  useEffect(() => {
    if (wordsFound > bestWords) {
      setBestWords(wordsFound);
      localStorage.setItem(BEST_SCORE_KEY, wordsFound.toString());
    }
  }, [wordsFound, bestWords]);

  // Load game history from localStorage
  useEffect(() => {
    const loadGameHistory = () => {
      const history = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith("slidewords-") && key !== BEST_SCORE_KEY) {
          const date = key.replace("slidewords-", "");
          try {
            const data = JSON.parse(localStorage.getItem(key));
            history[date] = {
              completed: data.completed,
              wordsFound: data.wordsFound,
              totalPossible: data.totalPossible,
              attempts: data.attempts,
            };
          } catch (e) {
            console.error("Error parsing game history:", e);
          }
        }
      }
      setGameHistory(history);
    };
    loadGameHistory();
  }, []);

  // Handlers for HUD
  const handleHelp = () => setShowHowTo(true);
  const handleStreakClick = () => setShowStreakModal(true);
  const handleDateClick = () => setShowCalendar(true);
  const handleMute = () => setIsMuted(!isMuted);

  // Callback for BaseSlidewords to update HUD state
  const handleGameUpdate = ({ score, guesses, attempts }) => {
    setWordsFound(score);
    setAttempts(attempts);
    // Store game data when score changes
    const dateKey = formatDateForStorage(currentDate);
    const currentGoal = calculateGoal(totalPossible);
    const gameData = {
      completed: score >= currentGoal, // Use dynamic goal instead of hardcoded 3
      wordsFound: score,
      totalPossible: totalPossible,
      attempts: attempts,
      date: currentDate.toISOString(),
    };
    localStorage.setItem(`slidewords-${dateKey}`, JSON.stringify(gameData));
    // Update game history
    setGameHistory((prev) => ({
      ...prev,
      [dateKey]: {
        completed: gameData.completed,
        wordsFound: gameData.wordsFound,
        totalPossible: gameData.totalPossible,
        attempts: gameData.attempts,
      },
    }));
    // Update streak if player wins
    if (score >= currentGoal && !isStreakActive) {
      const newStreak = updatePonderStreak(new Date(), GAME_ID);
      setStreak(newStreak);
      setIsStreakActive(true);
    }
  };

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setShowCalendar(false);
    // Reset game state for new date
    setWordsFound(0);
    setAttempts(0);
    setShowHowTo(false);
    setShowStreakModal(false);
  };

  return (
    <Box sx={{ bgcolor: "#f5f3ee", minHeight: "100vh", p: { xs: 0.5, sm: 1 } }}>
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: "sm",
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          bgcolor: "white",
          mx: "auto",
        }}
      >
        <SlidewordsHUD
          wordsFound={wordsFound}
          bestWords={bestWords}
          goalWords={calculateGoal(totalPossible)}
          currentDate={currentDate}
          streak={streak}
          isStreakActive={isStreakActive}
          onHelp={handleHelp}
          onDateClick={handleDateClick}
          onStreakClick={handleStreakClick}
          totalWords={totalPossible}
          isMuted={isMuted}
          onMute={handleMute}
        />
      </Paper>
      {loadingWords ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <BaseSlidewords
          customTitle="Slidewords"
          subtitle="Slide each word horizontally to form new words and find the target number!"
          wordList={sourceWords}
          goalWords={calculateGoal(totalPossible)}
          buttonColor="#4a90e2"
          modalBackgroundColor="#f5f3ee"
          backgroundColor="#f5f3ee"
          maxAttempts={30}
          onGameUpdate={handleGameUpdate}
          showHowToOverride={showHowTo}
          setShowHowToOverride={setShowHowTo}
          allPossibleWords={possibleWords}
          isMuted={isMuted}
        />
      )}
      <CalendarArchive
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        gameKey="slidewords"
        getGameDataForDate={(dateKey) => {
          const data = gameHistory[dateKey];
          if (!data) {
            return null;
          }
          const goalForDate = calculateGoal(data.totalPossible);
          return {
            completed: data.completed,
            goalMet: data.wordsFound >= goalForDate,
            wordsFound: data.wordsFound,
            totalPossible: data.totalPossible,
            attempts: data.attempts,
            best:
              data.wordsFound > 0
                ? `${data.wordsFound}/${data.totalPossible} words`
                : null,
          };
        }}
        formatDateForStorage={formatDateForStorage}
        title="Slidewords Archive"
        description="View your past Slidewords games and achievements"
        onDateSelect={handleDateSelect}
        currentDate={currentDate}
      />
    </Box>
  );
};

export default Slidewords;
